import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { Button } from '../../ui/Button';
import { Alert } from '../../ui/Alert';
import { followUpAiService } from '../../../services/followUpAiService';
import { followUpService } from '../../../services/followUpService';
import { opportunityWatchService } from '../../../services/opportunityWatchService';
import { MentionInput } from './MentionInput';
import { ContactPicker } from './ContactPicker';

const field = 'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-meetoi-navy';
const blankWatch = (text) => ({ title: '', instructions: text.slice(0, 400), trigger: '', frequency: 'monthly', stopDay: '', consent: false, clarification: '' });

export const AskFollowUp = ({ userId, contactId = null, momentId = null, onSaved }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [mentions, setMentions] = useState([]);

  useEffect(() => {
    let alive = true;
    followUpAiService.listContacts().then((list) => { if (alive) setContacts(list); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const reset = () => { setDraft(null); setText(''); setMentions([]); setError(''); };

  const addMention = (m) => setMentions((prev) => (prev.some((p) => p.id === m.id) ? prev : [...prev, m]));

  const prepare = async () => {
    if (busy || text.trim().length < 3) return;
    setBusy(true);
    setError('');
    try {
      const active = mentions.filter((m) => text.includes(`@${m.name}`));
      const res = await followUpAiService.prepare(text.trim(), active);
      setContacts(res.contacts || []);
      const wd = res.draft.watch;
      setDraft({
        note: res.draft.description || text.trim().slice(0, 200),
        day: res.draft.day || '',
        contactId: res.draft.contact_id || contactId || '',
        personName: res.draft.person_name,
        type: res.draft.type || 'followup',
        keySource: res.meta?.key_source,
        timezone: res.meta?.timezone,
        w: wd
          ? { title: wd.title, instructions: wd.instructions, trigger: wd.trigger, frequency: wd.frequency, stopDay: wd.stop_day || '', consent: false, clarification: wd.clarification || '' }
          : blankWatch(text.trim()),
        candidateIds: res.draft.candidate_ids || []
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const createContact = async (name) => {
    const created = await followUpAiService.createContact(userId, name);
    setContacts((prev) => [...prev, created]);
    setDraft((d) => ({ ...d, contactId: created.id }));
  };

  const save = async () => {
    if (saving || !draft.contactId || !draft.day || !draft.note.trim()) return;
    setSaving(true);
    setError('');
    try {
      await followUpService.create({
        userId,
        contactId: draft.contactId,
        day: draft.day,
        note: draft.note,
        momentId,
        source: 'ai_suggestion'
      });
      reset();
      onSaved?.('Follow-up added.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isWatch = draft?.type === 'watch';
  const byok = draft?.keySource === 'byok';
  const watchReady = isWatch && draft.w.title.trim() && draft.w.instructions.trim() && (!byok || draft.w.consent);

  const saveWatch = async () => {
    if (saving || !watchReady) return;
    setSaving(true);
    setError('');
    try {
      await opportunityWatchService.create({
        userId,
        contactId: draft.contactId || null,
        momentId,
        ...draft.w,
        timezone: draft.timezone,
        consent: draft.w.consent
      });
      reset();
      onSaved?.('Opportunity Watch started. Meetoi will check shortly, then on your chosen schedule.', 'watch');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setW = (k, v) => setDraft((d) => ({ ...d, w: { ...d.w, [k]: v } }));
  const ready = draft && draft.contactId && draft.day && draft.note.trim();

  return (
    <div className="mb-8 rounded-xl border border-violet-200 bg-violet-50/50 p-4">
      <p className="mb-2 flex items-center text-sm font-semibold text-violet-800">
        <SafeIcon icon={FiIcons.FiZap} className="mr-1.5" /> Ask AI to set a follow-up
      </p>
      {!draft && (
        <>
          <div className="flex flex-col gap-2 sm:flex-row">
            <MentionInput
              value={text}
              onChange={setText}
              contacts={contacts}
              onMention={addMention}
              onSubmit={prepare}
              placeholder="e.g. Remind me to email Rohan in a week about the car deal"
            />
            <Button onClick={prepare} isLoading={busy} disabled={text.trim().length < 3}>Prepare</Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">Tip: type @ to pick a contact.</p>
          <p className="mt-1 text-xs text-slate-500">To be alerted when new information appears, try: Watch for next year's Melbourne Design Week program.</p>
        </>
      )}
      {draft && (
        <div className="space-y-3">
          <p className="text-xs text-slate-600">Here is what Meetoi understood. Edit anything, then save. Nothing is saved yet.</p>
          <div className="flex gap-2">
            {[['followup', 'Follow-up'], ['watch', 'Opportunity Watch']].map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => set('type', k)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${draft.type === k ? 'border-violet-600 bg-violet-600 text-white' : 'border-slate-200 bg-white text-slate-600'}`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="text-xs font-medium text-slate-600">Who
            <ContactPicker
              contacts={contacts}
              value={draft.contactId}
              onChange={(id) => set('contactId', id)}
              candidateIds={draft.candidateIds}
              personName={draft.personName}
              onCreate={createContact}
            />
          </div>
          {isWatch && <p className="text-xs text-slate-500">A person is optional for an Opportunity Watch.</p>}
          {!isWatch && (
            <>
              <label className="block text-xs font-medium text-slate-600">What
                <input className={`${field} mt-1`} value={draft.note} maxLength={300} onChange={(e) => set('note', e.target.value)} />
              </label>
              <label className="block text-xs font-medium text-slate-600">When
                <input type="date" className={`${field} mt-1`} value={draft.day} onChange={(e) => set('day', e.target.value)} />
              </label>
              {!draft.day && <p className="text-xs text-amber-700">No date was clear from your sentence. Please choose one.</p>}
            </>
          )}
          {isWatch && (
            <>
              {draft.w.clarification && (
                <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                  Meetoi needs a little more detail: {draft.w.clarification} Fill in the subject below, or cancel and rephrase.
                </p>
              )}
              <label className="block text-xs font-medium text-slate-600">What Meetoi will monitor (organisation, event or programme)
                <input className={`${field} mt-1`} value={draft.w.title} maxLength={200} onChange={(e) => setW('title', e.target.value)} />
              </label>
              <label className="block text-xs font-medium text-slate-600">What to look for
                <textarea className={`${field} mt-1`} rows={2} value={draft.w.instructions} maxLength={1000} onChange={(e) => setW('instructions', e.target.value)} />
              </label>
              <label className="block text-xs font-medium text-slate-600">Alert me when
                <input className={`${field} mt-1`} value={draft.w.trigger} maxLength={500} placeholder="e.g. dates or bookings are officially announced" onChange={(e) => setW('trigger', e.target.value)} />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-xs font-medium text-slate-600">How often to check
                  <select className={`${field} mt-1`} value={draft.w.frequency} onChange={(e) => setW('frequency', e.target.value)}>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </label>
                <label className="block text-xs font-medium text-slate-600">Stop watching after (optional)
                  <input type="date" className={`${field} mt-1`} value={draft.w.stopDay} onChange={(e) => setW('stopDay', e.target.value)} />
                </label>
              </div>
              {momentId && <p className="text-xs text-slate-500">This watch will be linked to the current Moment.</p>}
              {byok ? (
                <label className="flex items-start gap-2 rounded-lg bg-white p-2 text-xs text-slate-700">
                  <input type="checkbox" className="mt-0.5" checked={draft.w.consent} onChange={(e) => setW('consent', e.target.checked)} />
                  I allow Meetoi to run scheduled checks in the background using my own OpenAI key. Each check uses web search and is billed to my OpenAI account (up to about 60 checks a month across all watches).
                </label>
              ) : (
                <p className="text-xs text-slate-500">Checks run in the background using Meetoi AI (limited to 30 checks a month). Meetoi only searches public web pages and alerts you when it finds something it can link to a source.</p>
              )}
            </>
          )}
          <div className="flex gap-2">
            {isWatch ? (
              <Button onClick={saveWatch} isLoading={saving} disabled={!watchReady}>Start watching</Button>
            ) : (
              <Button onClick={save} isLoading={saving} disabled={!ready}>Save follow-up</Button>
            )}
            <Button variant="secondary" onClick={reset} disabled={saving}>Cancel</Button>
          </div>
        </div>
      )}
      {error && <Alert variant="error" message={error} className="mt-3" />}
    </div>
  );
};